import type { KeyboardEvent } from "react";
import { Globe2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { LabelTooltip } from "./LabelTooltip";
import { CountryFlag } from "src/view/conponents/icons/CountryFlag";
import { PALETTE } from "./constants";
import { RANKING_FONT, rankingSeriesColor } from "./rankingTheme";
import { getRankingCountry } from "./rankingData";

type Layout = "inline" | "long" | "simple" | "many" | "country";

const List = styled.div<{ $layout: Layout; $seriesCount: number; $stretch?: boolean }>`
	min-width: 0;
	font: 13px/1.45 ${RANKING_FONT};
	color: #344054;
	display: grid;
	grid-template-columns: ${({ $layout, $seriesCount }) => $layout === "many" ? "minmax(0, 1fr)" : `${$layout === "country" ? "24px " : $layout === "inline" ? "auto " : ""}minmax(0, 1fr) repeat(${$seriesCount}, max-content)`};
	column-gap: 10px;
	row-gap: ${({ $layout }) => $layout === "country" ? "12px" : $layout === "inline" ? "8px" : "7px"};
	${({ $stretch }) => $stretch && "flex: 1; align-content: space-evenly;"}
	.label { display: flex; align-items: center; gap: 6px; min-width: 0; }
	.label > span { min-width: 0; }
	.country-flag { display: flex; align-items: center; justify-content: center; width: 24px; flex-shrink: 0; color: #98a2b3; }
	.track { display: flex; height: 7px; min-width: 0; border-radius: 3px; overflow: hidden; background: #eef1f5; }
	.segment { height: 100%; flex-shrink: 0; }
	.value { text-align: right; white-space: nowrap; font-size: 12px; font-weight: 500; font-variant-numeric: tabular-nums; }
	@media (max-width: 360px) { column-gap: 8px; }
	${({ $layout }) => $layout === "long" && ".label { grid-column: 1 / -1; }"}
	${({ $layout }) => $layout === "simple" && ".track { grid-column: 1 / -1; grid-row: 2; }"}
	${({ $layout }) => $layout === "country" && `
		.country-flag { grid-column: 1; grid-row: 1 / 3; }
		.label { grid-column: 2; grid-row: 1; }
		.value { grid-row: 1; }
		.track { grid-column: 2 / -1; grid-row: 2; height: 6px; }
	`}
	${({ $layout }) => $layout === "many" && ".values { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 2px 10px; }"}
`;

const Row = styled.div<{ $interactive: boolean; $layout: Layout }>`
	display: grid;
	grid-template-columns: subgrid;
	grid-column: 1 / -1;
	align-items: center;
	row-gap: ${({ $layout }) => $layout === "country" ? "5px" : $layout === "simple" ? "4px" : "1px"};
	min-width: 0;
	min-height: ${({ $layout }) => $layout === "inline" ? "28px" : "auto"};
	border-radius: 4px;
	${({ $interactive }) => $interactive && `
		cursor: pointer;
		&:hover { background: #f5f8fb; box-shadow: 4px 0 #f5f8fb, -4px 0 #f5f8fb; }
		&:focus-visible { outline: 2px solid #167ca6; outline-offset: 3px; }
		@media (pointer: coarse) { min-height: 44px; }
	`}
`;

interface SectionalBarItem {
	label: string;
	value: number[];
}

export interface SectionalBarListProps {
	data: SectionalBarItem[];
	colors: string[];
	labels?: string[];
	maxTotal: number;
	longLabels: boolean;
	stretch?: boolean;
	icon?: "country";
	single?: boolean;
	getRowAction?: (label: string) => { label: string; onClick: () => void } | undefined;
}

export const SectionalBarList = ({ data, colors, labels = [], maxTotal, longLabels, stretch, icon, single, getRowAction }: SectionalBarListProps) => {
	const { t, i18n } = useTranslation();
	const seriesCount = Math.max(1, labels.length, ...data.map(row => row.value.length));
	const layout: Layout = seriesCount > 2 ? "many" : icon === "country" ? "country" : !longLabels ? "inline" : single ? "simple" : "long";
	const palette = colors.length ? colors : PALETTE;
	const format = (value: number) => !Number.isFinite(value) ? "—" : value.toLocaleString(i18n.language, Math.abs(value) >= 100_000
		? { notation: "compact", maximumFractionDigits: 1 } : { maximumFractionDigits: 2 });
	const exact = (value: number) => Number.isFinite(value) ? value.toLocaleString(i18n.language, { maximumFractionDigits: 20 }) : "—";
	const scale = Number.isFinite(maxTotal) && maxTotal > 0 ? maxTotal : 1;
	if (data.length === 0) return <div role="status" style={{ color: "#667085", font: `13px/1.45 ${RANKING_FONT}` }}>{t("common.noData")}</div>;
	return <List $layout={layout} $seriesCount={seriesCount} $stretch={stretch}>
		{data.map((row, index) => {
			const action = getRowAction?.(row.label);
			const country = icon === "country" ? getRankingCountry(row.label, i18n.language) : undefined;
			const flag = country && <span className="country-flag" aria-hidden="true">{country.flagCode ? <CountryFlag code={country.flagCode} variant="rectangle" /> : <Globe2 size={18} />}</span>;
			const description = row.value.map((value, series) => `${labels[series] ?? t("stats.chart.series", { number: series + 1 })}: ${exact(Number(value))}`).join("; ");
			const values = Array.from({ length: seriesCount }, (_, series) => {
				const value = row.value[series] === undefined ? NaN : Number(row.value[series]);
				const label = labels[series] ?? t("stats.chart.series", { number: series + 1 });
				return <span className="value" key={series} style={{ color: single ? "#344054" : rankingSeriesColor(palette[series % palette.length], "text") }} title={`${label}: ${exact(value)}`} aria-label={`${label}: ${exact(value)}`}>{format(value)}</span>;
			});
			return <Row key={index} $layout={layout} $interactive={!!action}
				role={action ? "button" : undefined} tabIndex={action ? 0 : undefined}
				aria-haspopup={action ? "dialog" : undefined} aria-label={action ? `${action.label}. ${description}` : undefined}
				title={action?.label} onClick={action?.onClick}
				onKeyDown={action ? (event: KeyboardEvent<HTMLDivElement>) => {
					if (event.key === "Enter" || event.key === " ") { event.preventDefault(); action.onClick(); }
				} : undefined}>
				{layout === "country" && flag}
				<div className="label" title={country?.code ? `${country.name} (${country.code})` : undefined}>{layout !== "country" && flag}<LabelTooltip text={country?.name ?? row.label} style={{ fontFamily: RANKING_FONT, color: "#344054", lineHeight: 1.45, maxWidth: layout === "inline" ? 100 : undefined }} /></div>
				<div className="track" role="img" aria-label={`${row.label}. ${description}`}>
					{row.value.map((value, series) => <span className="segment" key={series} style={{ width: `${Number.isFinite(Number(value)) ? Math.min(100, Math.max(0, Number(value)) / scale * 100) : 0}%`, background: rankingSeriesColor(palette[series % palette.length]) }} />)}
				</div>
				{layout === "many" ? <div className="values">{values}</div> : values}
			</Row>;
		})}
	</List>;
};
